import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input, Textarea } from "./ui/input";
import { Badge } from "./ui/badge";
import { Sparkles, Send, BookOpen } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const SUGGESTED = [
  "Explain IFRS S2 climate disclosure pillars",
  "What's the Singapore carbon tax rate in 2026?",
  "Help me draft Scope 3 boundary statement",
  "How is Scope 2 location-based vs market-based?",
];

export function AIAssistant({ open, onOpenChange }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "I'm Lestari AI — an ESG advisor grounded in IFRS S2, GHG Protocol, SGX Core Metrics, SEFR and CPA. Ask me anything about climate disclosure, emissions accounting, or regulatory tiers.",
      citations: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (text) => {
    if (!text.trim() || busy) return;
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    try {
      const { data } = await api.post("/ai/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer, citations: data.citations || [] }]);
    } catch (e) {
      toast.error("AI request failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="p-0">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-accent flex items-center justify-center rounded-sm">
              <Sparkles className="h-4 w-4 text-black" />
            </div>
            <div>
              <SheetTitle>Lestari AI</SheetTitle>
              <SheetDescription>Grounded in IFRS S2 · GHG Protocol · SGX · SEFR</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto scrollbar-thin p-6 space-y-4" data-testid="ai-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-sm px-4 py-3 text-sm ${
                m.role === "user"
                  ? "bg-accent text-accent-foreground"
                  : "bg-surface border border-border"
              }`}>
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                {m.citations && m.citations.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border/50 flex flex-wrap gap-1.5">
                    {m.citations.map((c) => (
                      <span key={c.index} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-background border border-border rounded-sm font-mono text-[10px]">
                        <BookOpen className="h-2.5 w-2.5" /> [{c.index}] {c.framework}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-surface border border-border px-4 py-3 rounded-sm text-sm font-mono">
                <span className="inline-flex gap-1">
                  <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
                  <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" style={{ animationDelay: "150ms" }} />
                  <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" style={{ animationDelay: "300ms" }} />
                </span>
              </div>
            </div>
          )}
        </div>

        {messages.length <= 1 && (
          <div className="px-6 pb-3 border-t border-border pt-3">
            <div className="label-eyebrow mb-2">Suggested</div>
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTED.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="text-xs px-2 py-1 border border-border rounded-sm hover:bg-surface-hover transition-colors"
                  data-testid="ai-suggestion"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-border p-4 flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about ESG, emissions, IFRS S2…"
            rows={2}
            className="text-sm font-sans resize-none"
            data-testid="ai-input"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
          />
          <Button onClick={() => send(input)} disabled={busy || !input.trim()} variant="primary" data-testid="ai-send-btn">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
